import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { apiSlice } from '../../app/api/apiSlice';

// Adapter setup
const meditationConfigAdapter = createEntityAdapter({
  selectId: (config) => config._id,
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

const initialState = meditationConfigAdapter.getInitialState();

export const meditationApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // GET today's meditation config
    getTodayMeditationConfig: builder.query({
      query: () => '/api/v1/meditation/today',
      transformResponse: (response) => {
        console.log('meditation api data:', response);
        return response.meditation;
      },
      providesTags: ['Meditation'],
    }),

    // GET all configs (admin)
    getAllMeditationConfigs: builder.query({
      query: () => '/api/v1/admin/meditation/configs',
      transformResponse: (response) => {
        console.log('--- Inside transformResponse for getAllMeditationConfigs ---');
        console.log('Raw API response (full object):', response);

        if (response && Array.isArray(response.configs)) {
          const normalized = meditationConfigAdapter.setAll(initialState, response.configs);
          console.log('Normalized Data:', normalized);
          return normalized;
        } else {
          console.warn('Response configs invalid:', response);
          return initialState;
        }
      },
      providesTags: (result) => {
        console.log('--- Inside providesTags for getAllMeditationConfigs ---');
        if (result?.ids?.length) {
          return [
            { type: 'Meditation', id: 'LIST' },
            ...result.ids.map(id => ({ type: 'Meditation', id }))
          ];
        }
        return [{ type: 'Meditation', id: 'LIST' }];
      },
    }),

    // CREATE
    createMeditationConfig: builder.mutation({
      query: (newConfig) => ({
        url: '/api/v1/admin/meditation/config/new',
        method: 'POST',
        body: newConfig,
      }),
      invalidatesTags: [{ type: 'Meditation', id: 'LIST' }],
    }),
    getMeditationConfigById: builder.query({
      query: (id) => `/api/v1/admin/meditation/config/${id}`,
      providesTags: (result, error, id) => [{ type: 'Meditation', id }],
    }),

    // UPDATE
    updateMeditationConfig: builder.mutation({
      query: ({ id, configData }) => ({
        url: `/api/v1/admin/meditation/config/${id}`,
        method: 'PUT',
        body: configData,
      }),
      invalidatesTags: (res, err, { id }) => [{ type: 'Meditation', id }],
    }),

    // DELETE
    deleteMeditationConfig: builder.mutation({
      query: (id) => ({
        url: `/api/v1/admin/meditation/config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (res, err, id) => [
        { type: 'Meditation', id },
        { type: 'Meditation', id: 'LIST' },
      ],
    }),
  }),
});

// Export RTK Query Hooks
export const {
  useGetTodayMeditationConfigQuery,
  useGetAllMeditationConfigsQuery,
  useCreateMeditationConfigMutation,
  useUpdateMeditationConfigMutation,
  useDeleteMeditationConfigMutation,
  useGetMeditationConfigByIdQuery,
} = meditationApi;

//
// ✅ SELECTORS WITH ARGUMENT SUPPORT
//

// Step 1: Select the full result using the same cache key
// Selectors with argument support
export const selectMeditationConfigsResult = (state, arg = 'meditationList') =>
  meditationApi.endpoints.getAllMeditationConfigs.select(arg)(state);

export const selectMeditationConfigsData = createSelector(
  (state, arg = 'meditationList') => selectMeditationConfigsResult(state, arg),
  (result) => result?.data ?? meditationConfigAdapter.getInitialState()
);

// Manual selectors using entity adapter
export const selectMeditationConfigIds = (state, arg = 'meditationList') =>
  selectMeditationConfigsData(state, arg).ids;

export const makeSelectMeditationConfigById = (id, arg = 'meditationList') => (state) =>
  selectMeditationConfigsData(state, arg).entities[id];